import React from 'react';
import {
  Cog6ToothIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const FormSettingsPanel = ({ settings, onUpdateSettings, readOnly = false }) => {
  const handleUpdate = (updates) => {
    if (!readOnly && onUpdateSettings) {
      onUpdateSettings(updates);
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Basic Information
        </h4>
        
        <div className="space-y-4">
          {/* Form Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Form Name *
            </label>
            <input
              type="text"
              value={settings?.name || ''}
              onChange={(e) => handleUpdate({ name: e.target.value })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter form name"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={settings?.description || ''}
              onChange={(e) => handleUpdate({ description: e.target.value })}
              readOnly={readOnly}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Describe what this form is for"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              value={settings?.category || 'general'}
              onChange={(e) => handleUpdate({ category: e.target.value })}
              disabled={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="general">General</option>
              <option value="client_requests">Client Requests</option>
              <option value="support">Support</option>
              <option value="feedback">Feedback</option>
              <option value="consultation">Consultation</option>
              <option value="onboarding">Onboarding</option>
              <option value="contact">Contact</option>
              <option value="registration">Registration</option>
            </select>
          </div>

          {/* Default Form */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_default"
              checked={settings?.is_default || false}
              onChange={(e) => handleUpdate({ is_default: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="is_default" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Set as default form template
            </label>
          </div>
        </div>
      </div>

      {/* Submission Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Submission Settings
        </h4>
        
        <div className="space-y-4">
          {/* Submit Button Text */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Submit Button Text
            </label>
            <input
              type="text"
              value={settings?.submit_button_text || 'Submit'}
              onChange={(e) => handleUpdate({ submit_button_text: e.target.value })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Submit"
            />
          </div>

          {/* Success Message */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Success Message
            </label>
            <textarea
              value={settings?.success_message || ''}
              onChange={(e) => handleUpdate({ success_message: e.target.value })}
              readOnly={readOnly}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Thank you! Your submission has been received."
            />
          </div>

          {/* Allow Anonymous */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="allow_anonymous"
              checked={settings?.allow_anonymous || false}
              onChange={(e) => handleUpdate({ allow_anonymous: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="allow_anonymous" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Allow anonymous submissions
            </label>
          </div>
        </div>
      </div>

      {/* Task Creation Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Task Creation
        </h4>
        
        <div className="space-y-4">
          {/* Auto Assign Team */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Auto Assign to Team
            </label>
            <select
              value={settings?.auto_assign_team || 'support'}
              onChange={(e) => handleUpdate({ auto_assign_team: e.target.value })}
              disabled={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="support">Support Team</option>
              <option value="sales">Sales Team</option>
              <option value="design">Design Team</option>
              <option value="development">Development Team</option>
              <option value="management">Management</option>
              <option value="custom">Custom Assignment</option>
            </select>
          </div>

          {/* Auto Assign User */}
          {settings?.auto_assign_team === 'custom' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Auto Assign to User
              </label>
              <input
                type="text"
                value={settings?.auto_assign_user || ''}
                onChange={(e) => handleUpdate({ auto_assign_user: e.target.value })}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter user ID or email"
              />
            </div>
          )}

          {/* Default Priority */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Task Priority
            </label>
            <select
              value={settings?.default_priority || 'Normal'}
              onChange={(e) => handleUpdate({ default_priority: e.target.value })}
              disabled={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="Low">Low</option>
              <option value="Normal">Normal</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>

          {/* Task Category */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Task Category
            </label>
            <input
              type="text"
              value={settings?.default_task_category || ''}
              onChange={(e) => handleUpdate({ default_task_category: e.target.value })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="e.g., Client Request, Support, Bug Report"
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Notifications
        </h4>
        
        <div className="space-y-4">
          {/* Email Notifications */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="notification_email"
              checked={settings?.notification_email || false}
              onChange={(e) => handleUpdate({ notification_email: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="notification_email" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Send email notifications on form submission
            </label>
          </div>

          {/* Notification Recipients */}
          {settings?.notification_email && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notification Recipients
              </label>
              <input
                type="text"
                value={settings?.notification_recipients || ''}
                onChange={(e) => handleUpdate({ notification_recipients: e.target.value })}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                placeholder="email1@example.com, email2@example.com"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Comma-separated email addresses
              </p>
            </div>
          )}

          {/* Slack Notifications */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="notification_slack"
              checked={settings?.notification_slack || false}
              onChange={(e) => handleUpdate({ notification_slack: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="notification_slack" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Send Slack notifications
            </label>
          </div>

          {/* Slack Webhook */}
          {settings?.notification_slack && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slack Webhook URL
              </label>
              <input
                type="url"
                value={settings?.slack_webhook_url || ''}
                onChange={(e) => handleUpdate({ slack_webhook_url: e.target.value })}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                placeholder="https://hooks.slack.com/services/..."
              />
            </div>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Advanced Settings
        </h4>
        
        <div className="space-y-4">
          {/* Rate Limiting */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Submission Rate Limit (per hour)
            </label>
            <input
              type="number"
              value={settings?.rate_limit || ''}
              onChange={(e) => handleUpdate({ rate_limit: parseInt(e.target.value) || null })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="10"
              min="1"
              max="1000"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave empty for no limit
            </p>
          </div>

          {/* CAPTCHA */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="require_captcha"
              checked={settings?.require_captcha || false}
              onChange={(e) => handleUpdate({ require_captcha: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="require_captcha" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Require CAPTCHA verification
            </label>
          </div>

          {/* Form Expiration */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Form Expiration Date
            </label>
            <input
              type="datetime-local"
              value={settings?.expires_at || ''}
              onChange={(e) => handleUpdate({ expires_at: e.target.value })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave empty for no expiration
            </p>
          </div>

          {/* Response Limit */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Maximum Responses
            </label>
            <input
              type="number"
              value={settings?.max_responses || ''}
              onChange={(e) => handleUpdate({ max_responses: parseInt(e.target.value) || null })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="1000"
              min="1"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Form will be disabled after this many submissions
            </p>
          </div>
        </div>
      </div>

      {/* Help Information */}
      <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0 mt-0.5">
            <InformationCircleIcon className="h-4 w-4 text-blue-500" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
              Form Settings Tips
            </p>
            <ul className="text-xs text-blue-700 dark:text-blue-300 mt-1 space-y-1">
              <li>• Set meaningful names and descriptions for better organization</li>
              <li>• Enable anonymous submissions for public forms</li>
              <li>• Configure notifications to stay informed of new submissions</li>
              <li>• Use rate limits to prevent spam submissions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormSettingsPanel;